import { Mic, Volume2 } from 'lucide-react'
import {
  useResource,
  PageHeader, Card, Badge, LoadingState, ErrorState,
} from '@retink/web-ui'
import { api } from '../lib/api'

// Services > Channels > Voice — the tenant's effective voice settings. Read-only
// today: everything resolves to the platform defaults (the channels service owns
// the values; reads go SPA → /app/api/services/channels/voice → S2S → channels).
// Tenant voice profiles (named STT/TTS presets picked by flows, numbers, and
// voice-enabled agents) land on this page when they exist.

interface VoiceSettings {
  source: string
  stt: { provider: string; label: string }
  tts: { provider: string; label: string; voice: string }
}

export default function VoiceSettings() {
  const { data, error, loading, reload } = useResource<VoiceSettings>(
    () => api.get('/api/services/channels/voice'), [],
  )

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <PageHeader
        title="Voice"
        description="How calls on this tenant hear and are heard — the speech-to-text and text-to-speech configuration every voice flow uses."
      />
      {loading && <LoadingState label="Loading voice settings…" />}
      {error && <ErrorState message="Voice settings unavailable" onRetry={reload} />}
      {data && (
        <>
          <Card className="space-y-4 p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">Current configuration</h2>
              <Badge tone="neutral">
                {data.source === 'platform-default' ? 'Platform defaults' : data.source}
              </Badge>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-start gap-3">
                <Mic size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Speech-to-text</div>
                  <div className="text-sm font-medium">{data.stt.label}</div>
                  <div className="text-xs text-muted-foreground">{data.stt.provider}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Volume2 size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Text-to-speech</div>
                  <div className="text-sm font-medium">{data.tts.label}</div>
                  <div className="text-xs text-muted-foreground">{data.tts.provider} · {data.tts.voice}</div>
                </div>
              </div>
            </div>
          </Card>
          <p className="text-xs text-muted-foreground">
            These are the platform defaults, applied to every call. Tenant voice
            profiles — named presets that flows, numbers, and voice-enabled agents
            pick from — will be configured here.
          </p>
        </>
      )}
    </div>
  )
}
